<script setup>
import { reactive, computed, watch } from 'vue'

const props = defineProps({
    node: Object,
})

const emit = defineEmits(['update:config'])

function defaultPaths() {
    return [
        { id: 'a', label: 'A', percentage: 50 },
        { id: 'b', label: 'B', percentage: 50 },
    ]
}

const form = reactive({
    paths: (props.node?.data?.config?.paths ?? []).length > 0
        ? [...props.node.data.config.paths]
        : defaultPaths(),
})

watch(() => props.node, (node) => {
    if (!node) return
    form.paths = (node.data?.config?.paths ?? []).length > 0 ? [...node.data.config.paths] : defaultPaths()
})

const total = computed(() => form.paths.reduce((sum, p) => sum + Number(p.percentage), 0))
const isValid = computed(() => total.value === 100)

// Each path's id names its exit handle, and contacts are routed by it, so a
// new path takes the first letter no current path uses (removing b from
// a, b, c and adding again must not create a second c).
function nextPathId() {
    const used = new Set(form.paths.map(p => p.id))
    for (let i = 0; ; i++) {
        const id = i < 26 ? String.fromCharCode(97 + i) : `p${i}`
        if (!used.has(id)) return id
    }
}

function addPath() {
    const id = nextPathId()
    form.paths.push({ id, label: id.toUpperCase(), percentage: 0 })
    emitUpdate()
}

function removePath(index) {
    if (form.paths.length <= 2) return
    form.paths.splice(index, 1)
    emitUpdate()
}

function emitUpdate() {
    emit('update:config', { paths: form.paths.map(p => ({ ...p, percentage: Number(p.percentage) })) })
}
</script>

<template>
    <div class="config-panel__section">
        <label class="config-panel__label">Split Paths</label>
        <div class="split-config__paths">
            <div v-for="(path, index) in form.paths" :key="path.id" class="split-config__path">
                <input
                    v-model="path.label"
                    class="config-panel__input config-panel__input--sm"
                    type="text"
                    placeholder="Label"
                    @input="emitUpdate"
                    style="flex: 1"
                />
                <input
                    v-model="path.percentage"
                    class="config-panel__input config-panel__input--sm"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="%"
                    @input="emitUpdate"
                    style="width: 60px"
                />
                <span class="split-config__pct-label">%</span>
                <button
                    v-if="form.paths.length > 2"
                    class="condition-builder__remove-btn"
                    @click="removePath(index)"
                    type="button"
                >&times;</button>
            </div>
        </div>

        <div class="split-config__total" :class="{ 'split-config__total--error': !isValid }">
            Total: {{ total }}% <span v-if="!isValid">(must equal 100%)</span>
        </div>

        <button class="condition-builder__add-btn" @click="addPath" type="button">+ Add Path</button>
    </div>
</template>

<style scoped>
.split-config__paths {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 8px;
}
.split-config__path {
    display: flex;
    align-items: center;
    gap: 4px;
}
.split-config__pct-label {
    font-size: 12px;
    color: #6b7280;
}
.split-config__total {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 8px;
}
.split-config__total--error {
    color: #ef4444;
    font-weight: 600;
}
.condition-builder__remove-btn {
    background: none;
    border: none;
    color: #ef4444;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    padding: 0 2px;
}
.condition-builder__add-btn {
    background: none;
    border: 1px dashed #9ca3af;
    color: #6b7280;
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    width: 100%;
    transition: border-color 0.15s, color 0.15s;
}
.condition-builder__add-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
}
</style>
